import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "../../../UI/Modal/Modal";
import CloseButton from "../../../UI/CloseButton/CloseButton";

import InternetIcon from "../../../Icon-SVG/InternetIcon";
import VpnIcon from "../../../Icon-SVG/VpnIcon";
import AnalyserIcon from "../../../Icon-SVG/AnalyserIcon";
import DiskIcon from "../../../Icon-SVG/DiskIcon";
import MLSystemIcon from "../../../Icon-SVG/MLSystemIcon";
import TableSpinnerIcon from "../../../Icon-SVG/TableSpinnerIcon";
import DataRetentionIcon from "../../../Icon-SVG/DataRetentionIcon";
import DomainIcon from "../../../Icon-SVG/DomainIcon";
import CleanFileIcon from "../../../Icon-SVG/CleanFileIcon";
import HashIcon from "../../../Icon-SVG/HashIcon";
import MarketIcon from "../../../Icon-SVG/MarketIcon";

import { TriggerHealthCheck } from "../../../../store/actions/healthCheckActions";
import { RequestVms } from "../../../../store/actions/dashboardActions";

import "./styles.scss";

const ICON_SIZE = {
  width: 20,
  height: 20,
};

const SERVICES = [
  {
    id: "internet",
    clickable: true,
    header: "Internet",
    renderIcon: (color, width, height) => (
      <InternetIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "vpn",
    clickable: true,
    header: "VPN",
    renderIcon: (color, width, height) => <VpnIcon color={color} width={width} height={height} />,
  },
  {
    id: "analyser",
    clickable: true,
    header: "Analyser",
    renderIcon: (color, width, height) => (
      <AnalyserIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "reporter",
    clickable: true,
    header: "Reporter",
    renderIcon: (color, width, height) => (
      <AnalyserIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "broker",
    header: "Backend",
    renderIcon: (color, width, height) => (
      <AnalyserIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "disk",
    header: "Disk Size",
    renderIcon: (color, width, height) => <DiskIcon color={color} width={width} height={height} />,
  },
  {
    id: "fti",
    clickable: true,
    header: "FortressTI",
    renderIcon: (color, width, height) => (
      <DomainIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "ml",
    clickable: true,
    header: "ML System",
    renderIcon: (color, width, height) => (
      <MLSystemIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "cdr",
    header: "CDR",
    renderIcon: (color, width, height) => (
      <CleanFileIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "mongo",
    header: "MongoDB",
    renderIcon: (color, width, height) => (
      <DataRetentionIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "rabbit",
    header: "RabbitMQ",
    renderIcon: (color, width, height) => (
      <MarketIcon color={color} width={width} height={height} />
    ),
  },
  {
    id: "zsearch",
    header: "ZSearch",
    renderIcon: (color, width, height) => <HashIcon color={color} width={width} height={height} />,
  },
];

const SystemHealth = () => {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.healthCheck.loading);
  const coreResponsibles = useSelector((state) => state.healthCheck.coreResponsibles);
  const internetStatus = useSelector((state) => state.healthCheck.internetStatus);
  const vpnStatus = useSelector((state) => state.healthCheck.vpnStatus);
  const analyserStatus = useSelector((state) => state.healthCheck.analyserStatus);
  const reporterStatus = useSelector((state) => state.healthCheck.reporterStatus);
  const brokerStatus = useSelector((state) => state.healthCheck.brokerStatus);
  const diskStatus = useSelector((state) => state.healthCheck.diskStatus);
  const ftiStatus = useSelector((state) => state.healthCheck.ftiStatus);
  const mlStatus = useSelector((state) => state.healthCheck.mlStatus);
  const cdrStatus = useSelector((state) => state.healthCheck.cdrStatus);
  const rabbitStatus = useSelector((state) => state.healthCheck.rabbitStatus);
  const mongoStatus = useSelector((state) => state.healthCheck.mongoStatus);
  const zsearchStatus = useSelector((state) => state.healthCheck.zsearchStatus);

  const emptyDiskPercentage = useSelector((state) => state.healthCheck.emptyDiskPercentage);

  const [diskText, setDiskText] = useState("Disk Size");
  const [showListView, setShowListView] = useState(false);

  const getStatus = (id) => {
    let status = "close";
    switch (id) {
      case "internet":
        status = internetStatus;
        break;
      case "vpn":
        status = vpnStatus;
        break;
      case "analyser":
        status = analyserStatus;
        break;
      case "reporter":
        status = reporterStatus;
        break;
      case "broker":
        status = brokerStatus;
        break;
      case "disk":
        status = diskStatus;
        break;
      case "fti":
        status = ftiStatus;
        break;
      case "ml":
        status = mlStatus;
        break;
      case "cdr":
        status = cdrStatus;
        break;
      case "rabbit":
        status = rabbitStatus;
        break;
      case "zsearch":
        status = zsearchStatus;
        break;
      case "mongo":
        status = mongoStatus;
        break;
      default:
        status = "close";
    }
    return status;
  };

  const diskClickHandler = () => {
    if (diskText === "Disk Size") {
      setDiskText(emptyDiskPercentage);
    } else {
      setDiskText("Disk Size");
    }
  };

  useEffect(() => {
    dispatch(TriggerHealthCheck());
  }, [dispatch]);

  const refreshHandler = () => {
    dispatch(TriggerHealthCheck());
    dispatch(RequestVms());
  };

  const findClassName = (status) => {
    if (Array.isArray(status)) {
      const isAllTrue = status.every((s) => s === true);
      const isAllFalse = status.every((s) => s === false);
      const isAnyTrue = status.some((s) => s === true);
      return isAllTrue ? "open" : isAllFalse ? "close" : isAnyTrue ? "half" : "close";
    } else {
      return status ? "open" : "close";
    }
  };

  return (
    <div className="system-health">
      <div className="system-health__header">
        <h2 className="system-health__header__heading">Services</h2>
        <div className="system-health__header__refresh" onClick={refreshHandler}>
          <TableSpinnerIcon
            className="system-health__spinner"
            customStyle={{ margin: "0", height: "20px" }}
            spin={loading}
          />
        </div>
      </div>
      {SERVICES.map((service) => (
        <div
          className="system-health__block border"
          key={service.id}
          onClick={
            service.id === "disk"
              ? diskClickHandler
              : service.clickable
              ? () => setShowListView(true)
              : undefined
          }
          style={{ cursor: service.id === "disk" || service.clickable ? "pointer" : "default" }}>
          <div className="system-health__block--icon">
            {service.renderIcon("white", ICON_SIZE.width, ICON_SIZE.height)}
          </div>
          <p className="system-health__block--text" style={{ color: "white" }}>
            {service.id === "disk" ? diskText : service.header}
          </p>
          <div className="system-health__block--signal">
            <b
              className={`system-health__block--signal--status ${findClassName(
                getStatus(service.id),
              )}`}></b>
          </div>
        </div>
      ))}
      {showListView && (
        <Modal active={showListView} setActive={setShowListView} type="health-check-list-view">
          <div className="system-health__list-view border">
            <div className="system-health__list-view__close">
              <CloseButton
                onClick={() => {
                  setShowListView(false);
                }}
              />
            </div>
            <div className="system-health__list-view__container">
              <div className="system-health__list-view__headers">
                {coreResponsibles.map((cr, i) => (
                  <h6 key={i}>{cr}</h6>
                ))}
              </div>
              {SERVICES.filter((service) => service.clickable).map((service) => (
                <div className="system-health__multi" key={service.id}>
                  {getStatus(service.id)?.map((status, i) => (
                    <div className="system-health__block border" key={i}>
                      <div className="system-health__block--icon">
                        {service.renderIcon("white", ICON_SIZE.width, ICON_SIZE.height)}
                      </div>
                      <p className="system-health__block--text" style={{ color: "white" }}>
                        {service.header}
                        {i + 1}
                      </p>
                      <div className="system-health__block--signal">
                        <b
                          className={`system-health__block--signal--status ${findClassName(
                            status,
                          )}`}></b>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SystemHealth;
